import { Suspense, useState } from "react";
import {
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  styled,
  Toolbar,
  Typography,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useAppLayoutContext } from "hooks/routingHooks";

import { LayerTags } from "components/layers/LayerTags";
import { LayerCardsGrid } from "components/layers/LayerCardsGrid";
import { LayerCardSkeleton } from "components/skeletons/LayerCardSeleton";
import { IndicatorSearch } from "components/form/indicator/IndicatorSearch";

import { PaddedPaper } from "components/surfaces/PaddedPaper";
import { NavLink } from "components/navigation/NavLink";
import { IndicatorsCardsGrid } from "layout/IndicatorsCardsGrid";
import { random } from "lodash-es";

interface SearchForm {
  open: boolean;
  value: string;
}

export function IndicatorSelectionPage() {
  const { t } = useTranslation();
  const skeletonItems = new Array(random(8, 12)).fill("idk");
  const { config, settings } = useAppLayoutContext();
  const [pagerContainer, setPagerContainer] = useState<Element | null>(null);

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [search, setSearch] = useState<SearchForm>({ open: false, value: "" });

  return render();

  function render() {
    return (
      <Styles elevation={0}>
        <Toolbar className="grid-header">
          <Typography variant="subtitle2" className="spacing">
            {t("label.addAIndicator")}
          </Typography>
          <Collapse
            in={!showSearch}
            orientation="horizontal"
            className="spacing"
          >
            <IconButton onClick={() => setShowSearch(true)}>
              <Search />
            </IconButton>
          </Collapse>
          <Collapse
            in={showSearch}
            orientation="horizontal"
            className="spacing"
          >
            <IndicatorSearch
              className="disable-helpertext-margin spacing"
              search={search.value}
              setSearch={(value: string) =>
                setSearch({ ...search, value: value })
              }
              setInputRef={(ref) => ref?.focus()}
            />
          </Collapse>
          <IconButton
            edge="end"
            className="align-right"
            component={NavLink}
            to="../layers"
          >
            <Close />
          </IconButton>
        </Toolbar>

        {config && (
          <Suspense
            fallback={
              <Stack direction="row" spacing={1} className="tag-line">
                <Skeleton variant="chip" />
                <Skeleton variant="chip" />
                <Skeleton variant="chip" />
              </Stack>
            }
          >
            <LayerTags
              className="tag-line"
              config={config}
              search={search.value}
              selected={selectedTags}
              setSelected={setSelectedTags}
              layerProperties={settings.layerProperties}
            />
          </Suspense>
        )}
        {config && (
          <Suspense
            fallback={
              <IndicatorsCardsGrid>
                {skeletonItems.map((_, i) => (
                  <LayerCardSkeleton key={`layer-skeleton-loader-${i}`} />
                ))}
              </IndicatorsCardsGrid>
            }
          >
            {pagerContainer && (
              <LayerCardsGrid
                layerProperties={settings.layerProperties}
                config={config}
                search={search.value}
                selectedTags={selectedTags}
                pagerContainer={pagerContainer}
              />
            )}
          </Suspense>
        )}
        <div ref={(ref) => setPagerContainer(ref)}></div>
      </Styles>
    );
  }
}
const Styles = styled(PaddedPaper)`
  padding: ${({ theme }) => theme.spacing(2, 16)};
  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: ${({ theme }) => theme.spacing(2, 2)};
  }
  grid-area: content;
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: calc(100vh - 96px);

  .grid-header {
    position: sticky;
    top: ${({ theme }) => theme.spacing(-2)};
    z-index: 1;
    /* coloring trick to prevent grid from peeking through at the top when scrolling */
    background-color: ${({ theme }) => theme.palette.background.paper};
    padding: ${({ theme }) => theme.spacing(2, 0, 0, 0)};
    margin-top: ${({ theme }) => theme.spacing(-2)};
  }

  .tag-line {
    margin-bottom: ${({ theme }) => theme.spacing(2)};

    .MuiChip-root {
      margin-bottom: ${({ theme }) => theme.spacing(1)};
    }
  }

  .full-height {
    flex: 1 1 100%;
  }

  .MuiToolbar-root {
    .align-right {
      margin-left: auto;
    }

    .spacing + .spacing {
      margin-left: ${({ theme }) => theme.spacing(1)};
    }
  }
`;
